(function proteinEngineFactory(globalScope) {
  "use strict";

  var ALGORITHM_VERSION = "ALG-PROT-0.2-BETA";
  var METHODOLOGY_DATE = "31/07/2026";

  /*
   * Os coeficientes abaixo são constantes documentadas. O motor não usa IA,
   * texto generativo, API externa ou qualquer valor criado durante a execução.
   *
   * `reference` só existe quando há fonte específica para o ponto exibido.
   * Perfis sem esse apoio devolvem apenas a faixa publicada.
   */
  var PROFILES = Object.freeze({
    REF_HEALTHY_ADULT: Object.freeze({
      lower: 0.83,
      upper: 0.83,
      reference: 0.83,
      label: "Referência geral para adulto",
      evidenceIds: Object.freeze(["WHO-FAO-UNU-2007"]),
    }),
    WEIGHT_LOSS_GENERAL: Object.freeze({
      lower: 1.2,
      upper: 1.6,
      reference: null,
      label: "Perda de peso sem exercício estruturado",
      evidenceIds: Object.freeze(["LEIDY-2015"]),
    }),
    ACTIVE_LOW_FREQUENCY: Object.freeze({
      lower: 1.2,
      upper: 2.0,
      reference: null,
      label: "Exercício recreativo de baixa frequência",
      evidenceIds: Object.freeze(["ACSM-AND-DC-2016"]),
    }),
    ENDURANCE_RECREATIONAL: Object.freeze({
      lower: 1.2,
      upper: 2.0,
      reference: null,
      label: "Endurance recreativo",
      evidenceIds: Object.freeze(["ACSM-AND-DC-2016"]),
    }),
    RESISTANCE_RECREATIONAL: Object.freeze({
      lower: 1.4,
      upper: 2.0,
      reference: 1.6,
      label: "Treino resistido recreativo",
      evidenceIds: Object.freeze(["ISSN-2017", "MORTON-2018"]),
    }),
    MIXED_RECREATIONAL: Object.freeze({
      lower: 1.4,
      upper: 2.0,
      reference: null,
      label: "Treino misto recreativo",
      evidenceIds: Object.freeze(["ACSM-AND-DC-2016", "ISSN-2017"]),
    }),
    OTHER_RECREATIONAL: Object.freeze({
      lower: 1.2,
      upper: 2.0,
      reference: null,
      label: "Outro exercício recreativo",
      evidenceIds: Object.freeze(["ACSM-AND-DC-2016"]),
    }),
  });

  function parseLocaleNumber(value, options) {
    var normalized = String(value == null ? "" : value).trim();
    var integer = Boolean(options && options.integer);

    if (!/^\d+(?:[.,]\d+)?$/.test(normalized)) return null;

    var number = Number(normalized.replace(",", "."));
    if (!Number.isFinite(number)) return null;
    if (integer && !Number.isInteger(number)) return null;
    return number;
  }

  function roundHalfUpToFive(value) {
    return Math.floor(value / 5 + 0.5) * 5;
  }

  function stopped(status, messageKey) {
    return {
      status: status,
      messageKey: messageKey,
      algorithmVersion: ALGORITHM_VERSION,
      releaseStatus: "PUBLIC_BETA_RESTRICTED",
    };
  }

  function calculateProtein(input) {
    if (!input || typeof input !== "object") {
      return stopped("INPUT_INVALID", "invalid");
    }

    if (input.clinicalRoute === "STOP_CLINICAL") {
      return stopped("STOP_CLINICAL", "clinical");
    }
    if (input.clinicalRoute === "STOP_PROFESSIONAL_PLAN") {
      return stopped("STOP_PROFESSIONAL_PLAN", "professional");
    }
    if (input.clinicalRoute === "OUT_OF_SCOPE") {
      return stopped("OUT_OF_SCOPE", "scope");
    }
    if (input.clinicalRoute === "SPEC_PENDING") {
      return stopped("SPEC_PENDING", "pending");
    }
    if (input.clinicalRoute !== "ELIGIBLE_STANDARD") {
      return stopped("INPUT_INVALID", "invalid");
    }

    var age = parseLocaleNumber(input.ageYears, { integer: true });
    var weight = parseLocaleNumber(input.weightKg);
    var height = parseLocaleNumber(input.heightCm);
    var days = parseLocaleNumber(input.trainingDaysWeek, { integer: true });

    if (
      age === null ||
      age < 0 ||
      age > 120 ||
      weight === null ||
      weight < 25 ||
      weight > 500 ||
      height === null ||
      height < 100 ||
      height > 250 ||
      days === null ||
      days < 0 ||
      days > 7
    ) {
      return stopped("INPUT_INVALID", "invalid");
    }

    if (age < 18) return stopped("OUT_OF_SCOPE", "underage");
    if (age >= 65) return stopped("SPEC_PENDING", "olderAdult");
    if (input.trainingIsCompetitive) {
      return stopped("OUT_OF_SCOPE", "competitive");
    }
    if (
      (days === 0 && input.trainingType !== "none") ||
      (days > 0 && input.trainingType === "none")
    ) {
      return stopped("INPUT_INVALID", "trainingMismatch");
    }

    var heightM = height / 100;
    var bmiRaw = weight / (heightM * heightM);

    if (bmiRaw < 18.5) return stopped("STOP_CLINICAL", "lowBmi");
    if (bmiRaw >= 30) return stopped("SPEC_PENDING", "bmiOutsideBeta");
    if (input.usesWeightMedication === "yes") {
      return stopped("SPEC_PENDING", "weightMedication");
    }

    var profileCode;

    if (days === 0) {
      if (input.goal === "lose_weight") {
        if (bmiRaw >= 25) {
          profileCode = "WEIGHT_LOSS_GENERAL";
        } else {
          return stopped("SPEC_PENDING", "leanWeightLoss");
        }
      } else {
        profileCode = "REF_HEALTHY_ADULT";
      }
    } else if (days === 1) {
      profileCode = "ACTIVE_LOW_FREQUENCY";
    } else if (input.trainingType === "resistance") {
      profileCode = "RESISTANCE_RECREATIONAL";
    } else if (input.trainingType === "mixed") {
      profileCode = "MIXED_RECREATIONAL";
    } else if (input.trainingType === "endurance") {
      profileCode = "ENDURANCE_RECREATIONAL";
    } else if (input.trainingType === "other") {
      profileCode = "OTHER_RECREATIONAL";
    } else {
      return stopped("INPUT_INVALID", "trainingMismatch");
    }

    var profile = PROFILES[profileCode];
    var flags = [];

    if (
      input.goal === "gain_muscle" &&
      profileCode !== "RESISTANCE_RECREATIONAL" &&
      profileCode !== "MIXED_RECREATIONAL"
    ) {
      flags.push("GOAL_NOT_MATCHED_BY_RESISTANCE_TRAINING");
    }

    var gramsLowerRaw = weight * profile.lower;
    var gramsUpperRaw = weight * profile.upper;
    var gramsReferenceRaw =
      profile.reference === null ? null : weight * profile.reference;
    var isSingleReference = profile.lower === profile.upper;

    return {
      status: "ELIGIBLE_STANDARD",
      algorithmVersion: ALGORITHM_VERSION,
      releaseStatus: "PUBLIC_BETA_RESTRICTED",
      profileCode: profileCode,
      profileLabel: profile.label,
      weightBasis: "ACTUAL_WEIGHT",
      weightForCalculationKg: weight,
      bmiDisplay: Math.round(bmiRaw * 10) / 10,
      coefficientLower: profile.lower,
      coefficientUpper: profile.upper,
      referenceCoefficient: profile.reference,
      gramsLowerRaw: gramsLowerRaw,
      gramsUpperRaw: gramsUpperRaw,
      gramsReferenceRaw: gramsReferenceRaw,
      displayLowerG: isSingleReference
        ? null
        : roundHalfUpToFive(gramsLowerRaw),
      displayUpperG: isSingleReference
        ? null
        : roundHalfUpToFive(gramsUpperRaw),
      displayReferenceG:
        gramsReferenceRaw === null
          ? null
          : roundHalfUpToFive(gramsReferenceRaw),
      isSingleReference: isSingleReference,
      applicability:
        profileCode === "REF_HEALTHY_ADULT" ? "DIRECT" : "APPROXIMATE",
      flags: flags,
      evidenceIds: profile.evidenceIds.slice(),
    };
  }

  var api = Object.freeze({
    ALGORITHM_VERSION: ALGORITHM_VERSION,
    METHODOLOGY_DATE: METHODOLOGY_DATE,
    PROFILES: PROFILES,
    calculateProtein: calculateProtein,
    parseLocaleNumber: parseLocaleNumber,
    roundHalfUpToFive: roundHalfUpToFive,
  });

  globalScope.SuplementariaProteinEngine = api;
})(typeof window !== "undefined" ? window : globalThis);
